import React, { useEffect, useMemo, useRef, useState } from "react";
import style from "./FacilitySelect.module.scss";
import { getFacilityLabelByName } from "../../../utils";
import SearchIcon from "../../../static/images/search-icon.svg"
import ArrowDown from "../../../static/images/arrow_down_chevron_icon.svg"

export const FacilitySelect = ({ facilities, selectedFacility, onChange, loading }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => facilities.find((f) => f.facilityNumber === selectedFacility?.facilityNumber) ?? null,
    [facilities, selectedFacility?.facilityNumber]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return facilities;
    const q = query.trim().toLowerCase();
    return facilities.filter((f) => f.facilityNumber?.toLowerCase().includes(q) || f.facilityName?.toLowerCase().includes(q));
  }, [facilities, query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpen = () => {
    setOpen(true);
    setQuery("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSelect = (facility) => {
    if (facility.facilityNumber !== selectedFacility?.facilityNumber) {
      onChange(facility);
    }
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (e.key === "Enter" && filtered.length === 1) {
      handleSelect(filtered[0]);
    }
  };

  return (
    <div className={style.facilitySelect} ref={containerRef}>
      <button type="button" className={style.facilitySelectTrigger} onClick={() => (open ? setOpen(false) : handleOpen())} disabled={loading}>
        {loading ? (
          <span className={style.facilitySelectLoading}>
            <span className={style.custom_spinner} />
            Loading facilities…
          </span>
        ) : selected ? (
          <span className={style.facilitySelectValue}>
            <span>{getFacilityLabelByName(selected)}</span>
          </span>
        ) : (
          <span className={style.facilitySelectPlaceholder}>Select a facility</span>
        )}
        <img className={`${style.chevron} ${open ? style.chevronOpen : ""}`} src={ArrowDown} alt="Search" />
      </button>

      {open && (
        <div className={style.facilitySelectDropdown}>
          <div className={style.facilitySelectSearchWrap}>
            <img src={SearchIcon} className={style.searchIcon} alt="Search" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search by facility number or name"
              className={style.facilitySelectSearchInput}
            />
          </div>
          <ul className={style.facilitySelectList} role="listbox">
            {filtered.length === 0 ? (
              <li className={style.facilitySelectEmpty}>No facilities found</li>
            ) : (
              filtered.map((f) => (
                <li
                  key={f.id}
                  role="option"
                  aria-selected={f.facilityNumber === selectedFacility?.facilityNumber}
                  className={`${style.facilitySelectOption} ${
                    f.facilityNumber === selectedFacility?.facilityNumber ? style.facilitySelectOptionActive : ""
                  }`}
                  onClick={() => handleSelect(f)}
                >
                  <span>{getFacilityLabelByName(f)}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
